import { createContext, type ReactNode, useContext, useState } from 'react';

type PaginationMap = Record<string, number>;

type TokenMap = Record<string, Record<number, string | undefined>>;

interface PaginationContextValue {
  getPage: (key: string) => number;
  setPage: (key: string, page: number) => void;
  getToken: (key: string, page: number) => string | undefined;
  setToken: (key: string, page: number, token: string | undefined) => void;
}

const PaginationContext = createContext<PaginationContextValue | undefined>(
  undefined,
);

export const PaginationProvider = ({ children }: { children: ReactNode }) => {
  const [pages, setPages] = useState<PaginationMap>({});
  const [tokens, setTokens] = useState<TokenMap>({});

  const getPage = (key: string) => pages[key] ?? 1;

  const setPage = (key: string, page: number) => {
    setPages((prev) => ({ ...prev, [key]: page }));
  };

  const getToken = (key: string, page: number) => tokens[key]?.[page];

  const setToken = (key: string, page: number, token: string | undefined) => {
    setTokens((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] ?? {}),
        [page]: token,
      },
    }));
  };

  return (
    <PaginationContext.Provider
      value={{ getPage, setPage, getToken, setToken }}
    >
      {children}
    </PaginationContext.Provider>
  );
};

export const usePaginationContext = () => {
  const context = useContext(PaginationContext);
  if (!context) {
    throw new Error(
      'usePaginationContext must be used within a PaginationProvider',
    );
  }
  return context;
};
